import {
  ASPECT_RATIOS,
  createLazyLoadObserver,
  generateSrcSet,
  generateSizesAttribute,
  getLoadingAttribute,
  ImageLoadingState,
  ImageOptimizationParams,
  ImagePriority,
  optimizeImageUrl
} from '@/utils/imageOptimization';
import { sanitizeURL } from '@/utils/xssProtection';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface OptimizedImageProps {
  src: string;
  alt: string;
  fallback?: string;
  width?: number;
  height?: number;
  aspectRatio?: keyof typeof ASPECT_RATIOS | string;
  quality?: number;
  priority?: ImagePriority;
  enableWebP?: boolean;
  enableAVIF?: boolean;
  className?: string;
  onClick?: () => void;
  onLoad?: () => void;
  onError?: (error: Error) => void;
  sizes?: string;
  objectFit?: 'cover' | 'contain' | 'fill';
}

export const OptimizedImage = ({
  src,
  alt,
  fallback = '/default-image.png',
  width,
  height,
  aspectRatio,
  quality = 85,
  priority = 'auto',
  enableWebP = true,
  enableAVIF = false,
  className,
  onClick,
  onLoad,
  onError,
  sizes,
  objectFit = 'cover',
}: OptimizedImageProps) => {
  const [loadingState, setLoadingState] = useState<ImageLoadingState>('idle');
  const [imageSrc, setImageSrc] = useState<string>('');
  const [selectedFormat, setSelectedFormat] = useState<ImageOptimizationParams['format']>(undefined);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const shouldLazyLoad = priority !== 'high';

  // fetchpriority 속성을 DOM에 직접 설정 (React 타입 정의 우회)
  useEffect(() => {
    if (imgRef.current) {
      imgRef.current.setAttribute('fetchpriority', priority === 'high' ? 'high' : 'auto');
    }
  }, [priority]);

  useEffect(() => {
    const sanitizedSrc = sanitizeURL(src);
    if (!sanitizedSrc) {
      setLoadingState('error');
      setImageSrc(fallback);
      return;
    }

    // High priority 이미지는 즉시 로드
    if (priority === 'high') {
      loadImage(sanitizedSrc);
      return;
    }

    // Lazy loading 사용
    if (shouldLazyLoad && imgRef.current) {
      observerRef.current = createLazyLoadObserver((entry) => {
        if (entry.isIntersecting) {
          loadImage(sanitizedSrc);
          observerRef.current?.unobserve(entry.target);
        }
      });

      observerRef.current.observe(imgRef.current);
    } else {
      loadImage(sanitizedSrc);
    }

    return () => {
      if (observerRef.current && imgRef.current) {
        observerRef.current.unobserve(imgRef.current);
      }
    };
  }, [src, priority, fallback, shouldLazyLoad]);

  const loadImage = (url: string) => {
    setLoadingState('loading');

    // 지원 여부에 따라 최적 포맷 선택
    let format: ImageOptimizationParams['format'] = undefined;
    if (enableAVIF) {
      format = 'avif';
    } else if (enableWebP) {
      format = 'webp';
    }

    const params: ImageOptimizationParams = {
      width,
      height,
      quality,
      format,
      fit: objectFit,
    };

    const optimizedUrl = optimizeImageUrl(url, params);
    setImageSrc(optimizedUrl);
    setSelectedFormat(format);
  };

  const handleLoad = () => {
    setLoadingState('loaded');
    onLoad?.();
  };

  const handleError = () => {
    setLoadingState('error');
    setImageSrc(fallback);
    onError?.(new Error(`Failed to load image: ${src}`));
  };

  // 'auto' 포맷은 srcset에 사용 불가 → undefined로 처리
  const srcSetFormat = selectedFormat === 'auto' ? undefined : selectedFormat;
  const srcSet = width && src
    ? generateSrcSet(sanitizeURL(src) || '', width, undefined, srcSetFormat)
    : undefined;

  const computedSizes = sizes || (width ? generateSizesAttribute(width) : undefined);

  const computedAspectRatio = aspectRatio
    ? typeof aspectRatio === 'string' && aspectRatio in ASPECT_RATIOS
      ? ASPECT_RATIOS[aspectRatio as keyof typeof ASPECT_RATIOS]
      : aspectRatio
    : undefined;

  const decoding = priority === 'high' ? 'sync' : 'async';

  return (
    <ImageContainer
      className={className}
      $aspectRatio={computedAspectRatio}
      onClick={onClick}
    >
      <StyledImage
        ref={imgRef}
        src={imageSrc || fallback}
        alt={alt}
        srcSet={srcSet}
        sizes={computedSizes}
        loading={getLoadingAttribute(priority)}
        decoding={decoding}
        onLoad={handleLoad}
        onError={handleError}
        $isLoaded={loadingState === 'loaded'}
        $objectFit={objectFit}
        draggable={false}
      />

      {loadingState === 'loading' && (
        <LoadingOverlay>
          <LoadingSpinner />
        </LoadingOverlay>
      )}
    </ImageContainer>
  );
};

const ImageContainer = styled.div<{ $aspectRatio?: string }>`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  ${({ $aspectRatio }) =>
    $aspectRatio &&
    `
    aspect-ratio: ${$aspectRatio};
    height: auto;
  `}
`;

const StyledImage = styled.img<{
  $isLoaded: boolean;
  $objectFit: OptimizedImageProps['objectFit'];
}>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $objectFit }) => $objectFit || 'cover'};
  display: block;
  opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0)};
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.05);
  z-index: 2;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid ${({ theme }) => theme.colors.fontGray[300]};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
